
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Stack,
  Avatar,
  Chip,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  Business,
  Hotel,
  Restaurant,
  People,
  EventBusy,
  CalendarMonth,
  Phone,
  AccessTime,
  RestaurantMenu,
  LocalDining,
  TrendingUp,
  Bed,
  AddCircleOutline,
  WarningAmber,
  CheckCircle,
} from "@mui/icons-material";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";

const Dashboard = () => {
  const [operatorData, setOperatorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ===================== FETCH DASHBOARD DATA ===================== */

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date();

      const localCurrentDate =
        today.getFullYear() +
        "-" +
        String(today.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(today.getDate()).padStart(2, "0");

      const res = await axiosApi.post(
        "/dashBoard/getHostelOperatorSummaryDashboard",
        {
          currentDate: localCurrentDate,
        }
      );

      const dto = res.data ?? {};

      const mapped = [
        {
          operatorName: dto.operatorCompanyName ?? "Unknown Operator",

          hostels: Array.isArray(dto.hostelDashBoardDTOs)
            ? dto.hostelDashBoardDTOs.map((h) => ({
                id: h.hostelId ?? 0,
                name: h.hostelName ?? "Unnamed Hostel",
                mobile: h.mobile ?? "--",

                hostelClosure: h.hostelClosure === true,

                presentTodayMessCount:
                  h.presentTodayMessCount ?? 0,

                capacity: h.totalCapacity ?? 0,
                occupied: h.occupied ?? 0,

                messCountTotal: h.messCountTotal ?? 0,

                foodOnlyHostelers:
                  h.foodOnlyHostelers ?? 0,

                stayOnlyHostelers:
                  h.stayOnlyHostelers ?? 0,

                stayWithFoodHostelers:
                  h.stayWithFoodHostelers ?? 0,

                availableVacancy:
                  h.availableVacancy ?? 0,

                callbacks: h.callbacks ?? 0,
                visits: h.visits ?? 0,

                todayLeaveCount:
                  h.todayLeaveCount ?? 0,

                todayAbsentList: Array.isArray(
                  h.todayHostelerAbsentList
                )
                  ? h.todayHostelerAbsentList
                  : [],

                todayMenu: {
                  breakfast: h.todayBreakfastMenu || "N/A",
                  lunch: h.todayLunchMenu || "N/A",
                  snacks: h.todaySnacksMenu || "N/A",
                  dinner: h.todayDinnerMenu || "N/A",
                },

                timings: {
                  breakfast: h.todayBreakfastTime || "--",
                  lunch: h.todayLunchTime || "--",
                  snacks: h.todaySnacksTime || "--",
                  dinner: h.todayDinnerTime || "--",
                },

                monthlyBookingCount:
                  h.monthlyBookingCount ?? 0,

                todayAddOns: Array.isArray(
                  h.hostelAddOnMenuItemDTOs
                )
                  ? h.hostelAddOnMenuItemDTOs
                  : [],
              }))
            : [],
        },
      ];

      setOperatorData(mapped);
    } catch (e) {
      console.error("Dashboard loading error:", e);
      setError("Unable to load dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ===================== INITIAL LOAD ===================== */

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /* ===================== HOSTEL DATA ===================== */

const hostels = useMemo(
  () => operatorData?.[0]?.hostels || [],
  [operatorData]
);

const operatorName =
  operatorData?.[0]?.operatorName || "Unknown Operator";

  /* ===================== OVERALL TOTALS ===================== */

  const summary = useMemo(() => {
    const totalMessAttendance = hostels.reduce(
      (sum, h) =>
        sum + (Number(h.presentTodayMessCount) || 0),
      0
    );

    const totalAbsent = hostels.reduce(
      (sum, h) =>
        sum + (h.todayAbsentList?.length || 0),
      0
    );

    const totalCapacity = hostels.reduce(
      (sum, h) => sum + (Number(h.capacity) || 0),
      0
    );

    const totalOccupied = hostels.reduce(
      (sum, h) => sum + (Number(h.occupied) || 0),
      0
    );

    const totalVacancy = hostels.reduce(
      (sum, h) => sum + (Number(h.availableVacancy) || 0),
      0
    );

    const totalCallbacks = hostels.reduce(
      (sum, h) => sum + (Number(h.callbacks) || 0),
      0
    );

    const totalVisits = hostels.reduce(
      (sum, h) => sum + (Number(h.visits) || 0),
      0
    );

    const totalBookings = hostels.reduce(
      (sum, h) => sum + (Number(h.monthlyBookingCount) || 0),
      0
    );

    const occupancyPercent =
      totalCapacity > 0
        ? Math.round((totalOccupied / totalCapacity) * 100)
        : 0;

    return {
      totalMessAttendance,
      totalAbsent,
      totalCapacity,
      totalOccupied,
      totalVacancy,
      totalCallbacks,
      totalVisits,
      totalBookings,
      occupancyPercent,
    };
  }, [hostels]);

  /* ===================== REUSABLE KPI CARD ===================== */

  const KpiCard = ({
    title,
    value,
    subtitle,
    icon,
    color,
    background,
  }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid #E8EDF3",
        borderRadius: 3,
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Box minWidth={0}>
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              mb={1}
            >
              {title}
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              color="#172B4D"
            >
              {value}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.7}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          <Avatar
            sx={{
              width: 52,
              height: 52,
              flexShrink: 0,
              bgcolor: background,
              color,
              borderRadius: 2.5,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  /* ===================== SECTION TITLE ===================== */

  const SectionTitle = ({ title, subtitle, icon }) => (
    <Stack direction="row" alignItems="center" spacing={1.2} mb={2}>
      {icon && (
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: "#EEF2FF",
            color: "#3157C8",
            borderRadius: 2,
          }}
        >
          {icon}
        </Avatar>
      )}

      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={800}
          color="#172B4D"
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  /* ===================== SMALL DETAIL ITEM ===================== */

  const DetailRow = ({ label, value }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={1}
      sx={{ py: 0.8 }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 0 }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={700}
        color="#172B4D"
        textAlign="right"
        sx={{
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {value ?? "--"}
      </Typography>
    </Stack>
  );

  /* ===================== LOADING ===================== */

  if (loading) {
    return (
      <>
        <HeaderMenu />

        <Box
          minHeight="75vh"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap={2}
          bgcolor="#F5F7FB"
        >
          <CircularProgress size={48} />

          <Typography color="text.secondary" fontWeight={600}>
            Loading hostel dashboard...
          </Typography>
        </Box>
      </>
    );
  }

  /* ===================== ERROR ===================== */

  if (error) {
    return (
      <>
        <HeaderMenu />

        <Box
          minHeight="70vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="#F5F7FB"
          p={2}
        >
          <Card
            elevation={0}
            sx={{
              maxWidth: 460,
              width: "100%",
              textAlign: "center",
              p: 4,
              borderRadius: 3,
              border: "1px solid #E8EDF3",
            }}
          >
            <Avatar
              sx={{
                mx: "auto",
                mb: 2,
                bgcolor: "#FFF0ED",
                color: "#D84343",
                width: 56,
                height: 56,
              }}
            >
              <WarningAmber />
            </Avatar>

            <Typography fontWeight={800} color="#172B4D" mb={1}>
              Dashboard Loading Failed
            </Typography>

            <Typography color="text.secondary" variant="body2" mb={2.5}>
              {error}
            </Typography>

            <Button
              variant="contained"
              onClick={fetchDashboardData}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Try Again
            </Button>
          </Card>
        </Box>
      </>
    );
  }

  /* ===================== MAIN DASHBOARD ===================== */

  return (
    <>
      <HeaderMenu />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#F5F7FB",
          px: { xs: 1.5, sm: 2.5, md: 3.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        {/* ===================== PAGE HEADER ===================== */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
            mb: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 46,
                height: 46,
                bgcolor: "#E8EEFF",
                color: "#3157C8",
                borderRadius: 2,
              }}
            >
              <Business />
            </Avatar>

            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#172B4D"
              >
                Hostel Dashboard
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Monitor hostel operations, mess attendance and performance
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<Hotel />}
            label={`${hostels.length} Properties`}
            sx={{
              bgcolor: "#EAF0FF",
              color: "#3157C8",
              fontWeight: 700,
              borderRadius: 2,
            }}
          />
        </Box>

        {/* ===================== OPERATOR INFORMATION ===================== */}

        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: "1px solid #E8EDF3",
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
            >
              <Avatar
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: "#EAF0FF",
                  color: "#3157C8",
                  borderRadius: 2,
                }}
              >
                <Business />
              </Avatar>

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Operator / Company
                </Typography>

                <Typography
                  variant="h6"
                  fontWeight={800}
                  color="#172B4D"
                >
                  {operatorName}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ===================== OVERALL SUMMARY ===================== */}

        <Box mb={3}>
          <SectionTitle
            title="Overall Performance"
            subtitle="Combined operational summary across all your properties"
            icon={<TrendingUp />}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Mess Attendance Today"
                value={summary.totalMessAttendance}
                subtitle="Today's mess attendance"
                icon={<Restaurant />}
                color="#15966B"
                background="#E4F7EF"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Absent Today"
                value={summary.totalAbsent}
                subtitle="Total absent hostellers"
                icon={<EventBusy />}
                color="#D84343"
                background="#FFF0ED"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Total Beds"
                value={summary.totalCapacity}
                subtitle="Total property capacity"
                icon={<Bed />}
                color="#3157C8"
                background="#EAF0FF"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Occupied Beds"
                value={summary.totalOccupied}
                subtitle={`${summary.occupancyPercent}% overall occupancy`}
                icon={<People />}
                color="#8B5CC7"
                background="#F3EAFE"
              />
            </Grid>
          </Grid>
        </Box>

        {/* ===================== ADDITIONAL SUMMARY ===================== */}

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #E8EDF3",
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "#EAF0FF",
                    color: "#3157C8",
                    width: 40,
                    height: 40,
                  }}
                >
                  <Phone />
                </Avatar>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Monthly Callbacks
                  </Typography>

                  <Typography variant="h6" fontWeight={800}>
                    {summary.totalCallbacks}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #E8EDF3",
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "#FFF3DF",
                    color: "#D97706",
                    width: 40,
                    height: 40,
                  }}
                >
                  <People />
                </Avatar>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Monthly Visits
                  </Typography>

                  <Typography variant="h6" fontWeight={800}>
                    {summary.totalVisits}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #E8EDF3",
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "#E4F7EF",
                    color: "#15966B",
                    width: 40,
                    height: 40,
                  }}
                >
                  <CalendarMonth />
                </Avatar>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Monthly Bookings
                  </Typography>

                  <Typography variant="h6" fontWeight={800}>
                    {summary.totalBookings}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* ===================== NO HOSTELS ===================== */}

        {hostels.length === 0 && (
          <Card
            elevation={0}
            sx={{
              textAlign: "center",
              py: 6,
              borderRadius: 3,
              border: "1px solid #E8EDF3",
            }}
          >
            <Hotel
              sx={{
                fontSize: 45,
                color: "#94A3B8",
                mb: 1,
              }}
            />

            <Typography fontWeight={800} color="#172B4D">
              No hostel data found
            </Typography>

            <Typography variant="body2" color="text.secondary">
              No properties are currently available.
            </Typography>
          </Card>
        )}

        {/* ===================== HOSTEL-WISE DETAILS ===================== */}

        <Stack spacing={3}>
          {operatorData.map((op, operatorIndex) => (
            <Box key={`${op.operatorName}-${operatorIndex}`}>
              <SectionTitle
                title={op.operatorName}
                subtitle="Hostel-wise operational details"
                icon={<Business />}
              />

              <Grid container spacing={2.5}>
                {op.hostels.map((h) => {
                  const present = h.presentTodayMessCount ?? 0;
                  const total = h.messCountTotal ?? 0;

                  const eatingPercent =
                    total > 0
                      ? Number(((present / total) * 100).toFixed(1))
                      : 0;

                  const occupancy =
                    h.capacity > 0
                      ? Math.round((h.occupied / h.capacity) * 100)
                      : 0;

                  const capacityChartData = [
                    {
                      category: "Beds",
                      Capacity: h.capacity,
                      Occupied: h.occupied,
                      Vacancy: h.availableVacancy,
                    },
                    {
                      category: "Mess",
                      "Total Consumers": h.messCountTotal,
                      "Food Only": h.foodOnlyHostelers,
                      "Stay + Food": h.stayWithFoodHostelers,
                    },
                    {
                      category: "Stay",
                      "Stay Only": h.stayOnlyHostelers,
                    },
                  ];

                  return (
                    <Grid item xs={12} key={h.id}>
                      <Card
                        elevation={0}
                        sx={{
                          border: "1px solid #E5EAF1",
                          borderRadius: 3,
                          overflow: "hidden",
                          bgcolor: "#FFFFFF",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            boxShadow:
                              "0 8px 26px rgba(15,23,42,0.07)",
                          },
                        }}
                      >
                        {/* ===================== HOSTEL HEADER ===================== */}

                        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                          <Stack
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}
                            alignItems={{
                              xs: "flex-start",
                              sm: "center",
                            }}
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <Avatar
                                sx={{
                                  width: 48,
                                  height: 48,
                                  bgcolor: "#EAF0FF",
                                  color: "#3157C8",
                                  borderRadius: 2,
                                }}
                              >
                                <Hotel />
                              </Avatar>

                              <Box>
                                <Typography
                                  variant="h6"
                                  fontWeight={800}
                                  color="#172B4D"
                                >
                                  {h.name}
                                </Typography>

                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.7}
                                  mt={0.5}
                                >
                                  <Phone
                                    sx={{
                                      fontSize: 15,
                                      color: "#64748B",
                                    }}
                                  />

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {h.mobile}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>

                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              flexWrap="wrap"
                            >
                              {h.hostelClosure ? (
                                <Chip
                                  icon={<WarningAmber />}
                                  label="Hostel Closed Today"
                                  size="small"
                                  sx={{
                                    bgcolor: "#FFF0E0",
                                    color: "#C05B12",
                                    fontWeight: 700,
                                  }}
                                />
                              ) : (
                                <Chip
                                  icon={<CheckCircle />}
                                  label="Operational"
                                  size="small"
                                  sx={{
                                    bgcolor: "#E4F7EF",
                                    color: "#12805C",
                                    fontWeight: 700,
                                  }}
                                />
                              )}

                              <Chip
                                icon={<TrendingUp />}
                                label={`Occupancy ${occupancy}%`}
                                size="small"
                                sx={{
                                  bgcolor: "#EEF2FF",
                                  color: "#3157C8",
                                  fontWeight: 700,
                                }}
                              />
                            </Stack>
                          </Stack>

                          {/* OCCUPANCY PROGRESS */}

                          <Box mt={2}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              mb={0.8}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                Bed Occupancy
                              </Typography>

                              <Typography
                                variant="caption"
                                fontWeight={800}
                                color="#172B4D"
                              >
                                {h.occupied} / {h.capacity}
                              </Typography>
                            </Stack>

                            <LinearProgress
                              variant="determinate"
                              value={Math.min(
                                100,
                                Math.max(0, occupancy)
                              )}
                              sx={{
                                height: 8,
                                borderRadius: 5,
                                bgcolor: "#E9EEF5",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 5,
                                  bgcolor:
                                    occupancy >= 80
                                      ? "#15966B"
                                      : occupancy >= 50
                                      ? "#D97706"
                                      : "#3157C8",
                                },
                              }}
                            />
                          </Box>
                        </Box>

                        <Divider />

                        <CardContent
                          sx={{
                            p: { xs: 2, md: 2.5 },
                            "&:last-child": { pb: 2.5 },
                          }}
                        >
                          {/* HOSTEL CLOSURE WARNING */}

                          {h.hostelClosure && (
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                mb: 2.5,
                                bgcolor: "#FFF7ED",
                                border: "1px solid #FDBA74",
                                borderRadius: 2.5,
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <WarningAmber
                                  sx={{ color: "#E65100" }}
                                />

                                <Typography
                                  fontWeight={800}
                                  color="#C2410C"
                                >
                                  Hostel Closed Today
                                </Typography>
                              </Stack>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                mt={0.5}
                              >
                                No meals are served today due to hostel
                                closure.
                              </Typography>
                            </Paper>
                          )}

                          {/* ===================== MAIN HOSTEL SECTIONS ===================== */}

                          <Grid container spacing={2}>
                            {/* MESS ATTENDANCE AND MENU */}

                            <Grid item xs={12} md={4}>
                              <Card
                                elevation={0}
                                sx={{
                                  height: "100%",
                                  border: "1px solid #D7EFE2",
                                  borderRadius: 2.5,
                                  bgcolor: "#F5FCF8",
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <SectionTitle
                                    title="Mess Attendance Today"
                                    subtitle="Today's food consumption"
                                    icon={<Restaurant />}
                                  />

                                  <Typography
                                    variant="h3"
                                    fontWeight={900}
                                    color="#15966B"
                                  >
                                    {present}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="success.main"
                                    fontWeight={700}
                                  >
                                    {eatingPercent}% Consumed
                                  </Typography>

                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(
                                      100,
                                      Math.max(0, eatingPercent)
                                    )}
                                    sx={{
                                      mt: 1.5,
                                      mb: 2,
                                      height: 7,
                                      borderRadius: 5,
                                      bgcolor: "#DCEFE5",
                                      "& .MuiLinearProgress-bar": {
                                        bgcolor: "#15966B",
                                        borderRadius: 5,
                                      },
                                    }}
                                  />

                                  <Divider sx={{ mb: 1.5 }} />

                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    mb={1}
                                  >
                                    <RestaurantMenu
                                      sx={{
                                        fontSize: 19,
                                        color: "#15966B",
                                      }}
                                    />

                                    <Typography
                                      fontWeight={800}
                                      color="#172B4D"
                                    >
                                      Today's Menu
                                    </Typography>
                                  </Stack>

                                  <DetailRow
                                    label="☀ Breakfast"
                                    value={h.todayMenu.breakfast}
                                  />

                                  <DetailRow
                                    label="🍛 Lunch"
                                    value={h.todayMenu.lunch}
                                  />

                                  <DetailRow
                                    label="☕ Snacks"
                                    value={h.todayMenu.snacks}
                                  />

                                  <DetailRow
                                    label="🌙 Dinner"
                                    value={h.todayMenu.dinner}
                                  />

                                  <Divider sx={{ my: 1.5 }} />

                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    mb={1}
                                  >
                                    <AccessTime
                                      sx={{
                                        fontSize: 19,
                                        color: "#15966B",
                                      }}
                                    />

                                    <Typography
                                      fontWeight={800}
                                      color="#172B4D"
                                    >
                                      Serving Time
                                    </Typography>
                                  </Stack>

                                  <DetailRow
                                    label="Breakfast"
                                    value={h.timings.breakfast}
                                  />

                                  <DetailRow
                                    label="Lunch"
                                    value={h.timings.lunch}
                                  />

                                  <DetailRow
                                    label="Snacks"
                                    value={h.timings.snacks}
                                  />

                                  <DetailRow
                                    label="Dinner"
                                    value={h.timings.dinner}
                                  />
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* ABSENT TODAY */}

                            <Grid item xs={12} md={4}>
                              <Card
                                elevation={0}
                                sx={{
                                  height: "100%",
                                  border: "1px solid #F4DCDC",
                                  borderRadius: 2.5,
                                  bgcolor: "#FFFAFA",
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <SectionTitle
                                    title="Absent Today"
                                    subtitle="Today's attendance details"
                                    icon={<EventBusy />}
                                  />

                                  <Typography
                                    variant="h3"
                                    fontWeight={900}
                                    color="#D84343"
                                  >
                                    {h.todayAbsentList.length}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Total absent hostellers
                                  </Typography>

                                  <Divider sx={{ my: 2 }} />

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <Typography
                                      fontWeight={700}
                                      color="#172B4D"
                                    >
                                      Absent Hostellers
                                    </Typography>

                                    <Chip
                                      size="small"
                                      label={h.todayAbsentList.length}
                                      sx={{
                                        bgcolor: "#FFF0ED",
                                        color: "#D84343",
                                        fontWeight: 700,
                                      }}
                                    />
                                  </Stack>

                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      bgcolor: "#FFFFFF",
                                      border: "1px solid #F1E5E5",
                                      minHeight: 90,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color={
                                        h.todayAbsentList.length
                                          ? "#475569"
                                          : "success.main"
                                      }
                                      sx={{
                                        lineHeight: 1.8,
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {h.todayAbsentList.length
                                        ? h.todayAbsentList.join(", ")
                                        : "None"}
                                    </Typography>
                                  </Box>

                                  <Divider sx={{ my: 2 }} />

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                    >
                                      <EventBusy
                                        sx={{
                                          color: "#D97706",
                                          fontSize: 19,
                                        }}
                                      />

                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        Today's Leave Count
                                      </Typography>
                                    </Stack>

                                    <Typography
                                      fontWeight={800}
                                      color="#172B4D"
                                    >
                                      {h.todayLeaveCount}
                                    </Typography>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* MONTHLY SUMMARY */}

                            <Grid item xs={12} md={4}>
                              <Card
                                elevation={0}
                                sx={{
                                  height: "100%",
                                  border: "1px solid #F2E6D4",
                                  borderRadius: 2.5,
                                  bgcolor: "#FFFCF7",
                                }}
                              >
                                <CardContent sx={{ p: 2 }}>
                                  <SectionTitle
                                    title="Monthly Summary"
                                    subtitle="Monthly property performance"
                                    icon={<CalendarMonth />}
                                  />

                                  <Stack spacing={2}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: "#FFFFFF",
                                        border: "1px solid #F1E8DC",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          <Avatar
                                            sx={{
                                              width: 34,
                                              height: 34,
                                              bgcolor: "#FFF0DB",
                                              color: "#D97706",
                                            }}
                                          >
                                            <Phone fontSize="small" />
                                          </Avatar>

                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Callbacks
                                          </Typography>
                                        </Stack>

                                        <Typography
                                          variant="h6"
                                          fontWeight={800}
                                          color="#172B4D"
                                        >
                                          {h.callbacks}
                                        </Typography>
                                      </Stack>
                                    </Paper>

                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: "#FFFFFF",
                                        border: "1px solid #F1E8DC",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          <Avatar
                                            sx={{
                                              width: 34,
                                              height: 34,
                                              bgcolor: "#EAF0FF",
                                              color: "#3157C8",
                                            }}
                                          >
                                            <People fontSize="small" />
                                          </Avatar>

                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Visits
                                          </Typography>
                                        </Stack>

                                        <Typography
                                          variant="h6"
                                          fontWeight={800}
                                          color="#172B4D"
                                        >
                                          {h.visits}
                                        </Typography>
                                      </Stack>
                                    </Paper>

                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: "#FFFFFF",
                                        border: "1px solid #F1E8DC",
                                        borderRadius: 2,
                                      }}
                                    >
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                      >
                                        <Stack
                                          direction="row"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          <Avatar
                                            sx={{
                                              width: 34,
                                              height: 34,
                                              bgcolor: "#E4F7EF",
                                              color: "#15966B",
                                            }}
                                          >
                                            <CalendarMonth fontSize="small" />
                                          </Avatar>

                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Monthly Bookings
                                          </Typography>
                                        </Stack>

                                        <Typography
                                          variant="h6"
                                          fontWeight={800}
                                          color="#172B4D"
                                        >
                                          {h.monthlyBookingCount}
                                        </Typography>
                                      </Stack>
                                    </Paper>
                                  </Stack>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>

                          {/* ===================== TODAY'S ADD-ON ITEMS ===================== */}

                          {h.todayAddOns.length > 0 && (
                            <Box mt={2.5}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: { xs: 2, sm: 2.5 },
                                  borderRadius: 2.5,
                                  bgcolor: "#FFFCF7",
                                  border: "1px solid #F4DFC0",
                                }}
                              >
                                <Stack
                                  direction={{
                                    xs: "column",
                                    sm: "row",
                                  }}
                                  justifyContent="space-between"
                                  alignItems={{
                                    xs: "flex-start",
                                    sm: "center",
                                  }}
                                  spacing={1}
                                  mb={0.5}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: "#FFF0DB",
                                        color: "#D97706",
                                      }}
                                    >
                                      <AddCircleOutline />
                                    </Avatar>

                                    <Typography
                                      fontWeight={800}
                                      color="#172B4D"
                                    >
                                      Today's Add-On Items
                                    </Typography>
                                  </Stack>

                                  <Chip
                                    label="EXTRA"
                                    size="small"
                                    sx={{
                                      bgcolor: "#FFF0DB",
                                      color: "#C05B12",
                                      fontWeight: 800,
                                      borderRadius: 1.5,
                                    }}
                                  />
                                </Stack>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  mt={1}
                                  mb={1.5}
                                >
                                  Extra items · Not included in today's menu ·
                                  Charged separately
                                </Typography>

                                <Divider sx={{ mb: 1.5 }} />

                                <Grid container spacing={1.5}>
                                  {h.todayAddOns.map((item, idx) => (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={idx}
                                    >
                                      <Paper
                                        elevation={0}
                                        sx={{
                                          p: 1.5,
                                          bgcolor: "#FFFFFF",
                                          border: "1px solid #F1E8DC",
                                          borderRadius: 2,
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          spacing={1}
                                        >
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            minWidth={0}
                                          >
                                            <LocalDining
                                              sx={{
                                                color: "#D97706",
                                                fontSize: 19,
                                                flexShrink: 0,
                                              }}
                                            />

                                            <Typography
                                              variant="body2"
                                              fontWeight={600}
                                              sx={{
                                                overflowWrap: "anywhere",
                                              }}
                                            >
                                              {item.itemName}
                                            </Typography>
                                          </Stack>

                                          <Chip
                                            label={`₹${item.itemPrice}`}
                                            size="small"
                                            sx={{
                                              bgcolor: "#E4F7EF",
                                              color: "#12805C",
                                              fontWeight: 800,
                                              flexShrink: 0,
                                            }}
                                          />
                                        </Stack>
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Paper>
                            </Box>
                          )}

                          {/* ===================== CAPACITY AND MESS CHART ===================== */}

                          <Box mt={2.5}>
                            <Card
                              elevation={0}
                              sx={{
                                border: "1px solid #E8EDF3",
                                borderRadius: 2.5,
                              }}
                            >
                              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <SectionTitle
                                  title="Hostel Capacity & Mess Overview"
                                  subtitle="Beds, occupancy, vacancy and mess consumption breakdown"
                                  icon={<TrendingUp />}
                                />

                                <Box
                                  sx={{
                                    width: "100%",
                                    height: { xs: 300, md: 340 },
                                    minWidth: 0,
                                  }}
                                >
                                  <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                  >
                                    <BarChart
                                      data={capacityChartData}
                                      margin={{
                                        top: 10,
                                        right: 10,
                                        left: 0,
                                        bottom: 5,
                                      }}
                                    >
                                      <CartesianGrid
                                        strokeDasharray="3 3"
                                        vertical={false}
                                        stroke="#E8EDF3"
                                      />

                                      <XAxis
                                        dataKey="category"
                                        tick={{
                                          fill: "#64748B",
                                          fontSize: 12,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                      />

                                      <YAxis
                                        allowDecimals={false}
                                        tick={{
                                          fill: "#64748B",
                                          fontSize: 11,
                                        }}
                                        axisLine={false}
                                        tickLine={false}
                                      />

                                      <Tooltip
                                        contentStyle={{
                                          borderRadius: 10,
                                          border: "1px solid #E2E8F0",
                                          boxShadow:
                                            "0 4px 14px rgba(0,0,0,0.08)",
                                        }}
                                      />

                                      <Legend
                                        wrapperStyle={{
                                          fontSize: 11,
                                          paddingTop: 12,
                                        }}
                                      />

                                      {/* Beds */}

                                      <Bar
                                        dataKey="Capacity"
                                        fill="#90CAF9"
                                        radius={[4, 4, 0, 0]}
                                      />

                                      <Bar
                                        dataKey="Occupied"
                                        fill="#66BB6A"
                                        radius={[4, 4, 0, 0]}
                                      />

                                      <Bar
                                        dataKey="Vacancy"
                                        fill="#EF5350"
                                        radius={[4, 4, 0, 0]}
                                      />

                                      {/* Mess */}

                                      <Bar
                                        dataKey="Total Consumers"
                                        fill="#FFB74D"
                                        radius={[4, 4, 0, 0]}
                                      />

                                      <Bar
                                        dataKey="Food Only"
                                        fill="#42A5F5"
                                        radius={[4, 4, 0, 0]}
                                      />

                                      <Bar
                                        dataKey="Stay + Food"
                                        fill="#81C784"
                                        radius={[4, 4, 0, 0]}
                                      />

                                      {/* Stay */}

                                      <Bar
                                        dataKey="Stay Only"
                                        fill="#CE93D8"
                                        radius={[4, 4, 0, 0]}
                                      />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </Box>
                              </CardContent>
                            </Card>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Stack>
      </Box>
    </>
  );
};

export default Dashboard;
